import { useState, useMemo, useCallback } from 'react'
import { useAuth } from '@/modules/auth'
import { useSubscription } from '@/modules/subscriptions/hooks/useSubscription'
import { RangerApi } from '@/shared/api/ranger'
import { ApiError } from '@/shared/api/types'

export interface GenerateTripDescriptionParams {
  sentence: string
  tripId?: string
  tripName?: string
  startDate?: string
  endDate?: string
}

export function useGenerateTripDescription() {
  const { getAccessToken } = useAuth()
  const { canUseRanger } = useSubscription()
  const rangerApi = useMemo(() => new RangerApi(getAccessToken), [getAccessToken])
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generate = useCallback(
    async (params: GenerateTripDescriptionParams): Promise<string | null> => {
      setError(null)
      if (!canUseRanger) {
        return null
      }
      setIsGenerating(true)
      try {
        const { description } = await rangerApi.generateTripDescription({
          sentence: params.sentence.trim(),
          tripId: params.tripId,
          tripName: params.tripName?.trim() || undefined,
          startDate: params.startDate || undefined,
          endDate: params.endDate || undefined,
        })
        return description
      } catch (e) {
        let message = 'Failed to generate description. Please try again.'
        if (e instanceof ApiError) {
          if (e.status === 403) {
            message = 'Ranger AI requires a Pro subscription.'
          } else {
            message = e.message || message
          }
        }
        setError(message)
        return null
      } finally {
        setIsGenerating(false)
      }
    },
    [canUseRanger, rangerApi]
  )

  const clearError = useCallback(() => setError(null), [])

  return { generate, isGenerating, error, clearError, canUseRanger }
}
